import { Injectable } from "@angular/core";
import { FileLogger } from "@comunicare/comunicare-helper";
import { ALERT_STATUS, IRule, IRuleAlert, IRuleDescription } from "@comunicare/comunicare-interfaces";
import { TranslateService } from "@ngx-translate/core";
import { Observable, of } from "rxjs";
import { concatMap, first, map } from "rxjs/operators";
import { RuleAlert } from "../models/rule-alert.model";
import { IRuleAlertsInfosParams, RuleAlertApiService } from "./api/rule-alert-api.service";
import { UserService } from "./user.service";

export { IRuleAlertsInfosParams } from "./api/rule-alert-api.service";

@Injectable({
  providedIn: "root",
})
export class RuleAlertService {
  constructor(
    private ruleAlertApiService: RuleAlertApiService,
    private userService: UserService,
    private translateService: TranslateService
  ) {}

  public canUpdateSync(): boolean {
    return this.userService.isAuthorizedSync(null, this.ruleAlertApiService.updateRoutes[0], "PUT");
  }

  public list(
    alertLevel = 1,
    services: string[],
    monitoringIds: string[],
    onlyNoDataTransmissionAlert: boolean,
    alertStatus: ALERT_STATUS
  ): Observable<RuleAlert[]> {
    const routeName = this.ruleAlertApiService.readRoutes[0];
    return this.userService.isAuthorized(routeName, "GET").pipe(
      concatMap((isAuth) => {
        if (!isAuth) {
          FileLogger.warn("RuleAlertService", "User does not have access to: GET " + routeName);
          return of([]) as Observable<RuleAlert[]>;
        }
        return this.ruleAlertApiService.list(alertLevel, services, monitoringIds, onlyNoDataTransmissionAlert, alertStatus).pipe(
          map((values) => {
            return values.map((v) => new RuleAlert(v, this.translateService));
          })
        );
      })
    );
  }

  public listInfos(params: IRuleAlertsInfosParams): Observable<RuleAlert[]> {
    const routeName = this.ruleAlertApiService.readRoutes[3];
    return this.userService.isAuthorized(routeName, "GET").pipe(
      concatMap((isAuth) => {
        if (!isAuth) {
          FileLogger.warn("RuleAlertService", "User does not have access to: GET " + routeName);
          return of([]) as Observable<RuleAlert[]>;
        }
        return this.ruleAlertApiService.listInfos(params).pipe(
          map((values) => {
            return values.map((v) => new RuleAlert(v, this.translateService));
          })
        );
      })
    );
  }

  public countInfos(params: IRuleAlertsInfosParams): Observable<number> {
    const routeName = this.ruleAlertApiService.readRoutes[3];
    return this.userService.isAuthorized(routeName, "GET").pipe(
      concatMap((isAuth) => {
        if (!isAuth) {
          FileLogger.warn("RuleAlertService", "User does not have access to: GET " + routeName);
          return of(0);
        }
        return this.ruleAlertApiService.countInfos(params).pipe(map((result) => result.count));
      })
    );
  }

  public save(ruleAlert: RuleAlert): Observable<IRuleAlert[]> {
    return this.ruleAlertApiService.save(ruleAlert);
  }

  public listForPatient(patientId: string, alertsLevels?: string[]): Observable<RuleAlert[]> {
    const routeName = this.ruleAlertApiService.readRoutes[0];
    return this.userService.isAuthorized(routeName, "GET").pipe(
      concatMap((isAuth) => {
        if (!isAuth) {
          FileLogger.warn("RuleAlertService", "User does not have access to: GET " + routeName);
          return of([]) as Observable<RuleAlert[]>;
        }
        return this.ruleAlertApiService.listForPatient(patientId, alertsLevels).pipe(
          map((values) => {
            return values.map((v) => new RuleAlert(v, this.translateService));
          })
        );
      })
    );
  }

  public listNoDataTransmission(caremateId: string): Observable<RuleAlert> {
    const routeName = this.ruleAlertApiService.readRoutes[1];
    return this.userService.isAuthorized(routeName, "GET").pipe(
      first(),
      concatMap((isAuth) => {
        if (!isAuth) {
          FileLogger.warn("RuleAlertService", "User does not have access to: GET " + routeName);
          return of(null) as Observable<RuleAlert>;
        }
        return this.ruleAlertApiService.listNoDataTransmission(caremateId).pipe(
          map((v) => {
            return new RuleAlert(v, this.translateService);
          })
        );
      })
    );
  }

  /**
   * Creates a new rule
   * @param rule The new rule to save as an IRule
   * @returns The saved rule
   */
  public createRule(rule: IRule): Observable<IRule> {
    return this.ruleAlertApiService.createRule(rule);
  }

  /**
   * Get rules description
   * @param identifier The patient caremateId
   * @param lang
   * @returns The list of rules descriptions
   */
  public getRulesDescriptions(identifier: string, lang: string): Observable<IRuleDescription[]> {
    return this.ruleAlertApiService.getRulesDescriptions(identifier, lang);
  }
}
